import { ClearOutlined, CloseOutlined, CopyOutlined, DownloadOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Col, DatePicker, Drawer, Form, Image, Modal, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import api from '../../api/api';
import { errorHandler, mobileNumber } from '../../utils/helper';
import PrimaryButton, { SecondaryButton } from '../UI/Button/Button';
import TableComponent from '../UI/Table/TableComponent';
import { Cancel, Copy, Mail, Phone } from 'iconoir-react';
import BottomDrawer from '../UI/Drawer/BottomDrawer';
import useIsMobile from '../../hooks/useIsMobile';
import { successNotification } from '../UI/Toast/Toast';
import './_custommodal.scss';

export { PDFViewerModal } from './PDFViewerModal';
export { InstructionsModal } from './InstructionsModal';
export { ErrorModal } from './ErrorModal';

interface Props {
  isModalVisible: boolean;
  modalTitle?: string;
  cancelHandler: () => void;
  confirmHandler?: any;
  type?: string;
  data?: any;
  title?: string;
  subtitle?: string;
  modalButtonLoading?: boolean;
  service?: string;
  stepsToAvail?: any;
  description?: string;
  product?: any;
  logo?: string;
  setData?: any;
  availType?: string;
  coupon_code?: string;
  getSubscriptions?: any;
  cancelButtonText?: string;
  confirmButtonText?: string;
  children?: any;
  onViewDocument?: () => void;
  onViewInstructions?: () => void;
}

export const CustomModal: React.FC<Props> = ({
  isModalVisible,
  confirmHandler,
  cancelHandler,
  title,
  subtitle,
  type,
  modalButtonLoading,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  data,
  children,
}) => {
  if (type === 'confirm-bulk-addition') {
    return (
      <Modal
        visible={isModalVisible}
        footer={null}
        onCancel={cancelHandler}
        className="confirm-bulk-addition"
        closable={false}
        centered
      >
        <Row align="middle">
          <Col span={24}>
            <div className="modal-title">{title}</div>
          </Col>
        </Row>
        <div className="modal-subtitle">
          {subtitle}
          <br />
          <ul className="mt-16">
            {data?.map((item: any, index: number) => {
              return (
                <li key={index}>
                  {item?.key} : {item?.value}
                </li>
              );
            })}
          </ul>
        </div>
        <Row justify="end" className="mt-56">
          <SecondaryButton onClick={cancelHandler}>{cancelButtonText}</SecondaryButton>
          <PrimaryButton onClick={confirmHandler} className="margin-left" loading={modalButtonLoading}>
            {confirmButtonText}
          </PrimaryButton>
        </Row>
      </Modal>
    );
  }
  else {
    return (
      <Modal visible={isModalVisible} footer={null} onCancel={cancelHandler} centered>
        <Row justify="space-around" align="middle">
          <Col span={24} className="modal-card">
            <div className="modal-icon">
              {type === 'confirm' ? <ExclamationCircleOutlined /> : type === 'delete' ? <ClearOutlined /> : null}
            </div>
            <div className="modal-title">{title}</div>
            <div className="modal-subtitle">{subtitle}</div>
            {children && <div>{children}</div>}
          </Col>
        </Row>
        <div className="modal-footer">
          <SecondaryButton onClick={cancelHandler}>{cancelButtonText}</SecondaryButton>
          <PrimaryButton onClick={confirmHandler} className="margin-left" loading={modalButtonLoading}>
            {confirmButtonText}
          </PrimaryButton>
        </div>
      </Modal>
    );
  }
};


export const ReviewBulkInviteModal: React.FC<Props> = ({
  isModalVisible,
  confirmHandler,
  cancelHandler,
  title,
  subtitle,
  modalButtonLoading,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Confirm',
  data,
}) => {
  return (
    <Modal
      visible={isModalVisible}
      footer={null}
      onCancel={cancelHandler}
      className="confirm-bulk-invitation"
      closable={false}
      centered
    >
      <Row align="middle">
        <Col span={24}>
          <div className="modal-title">{title}</div>
        </Col>
      </Row>
      <div className='mt-24'>{subtitle}</div>
      <div className="mt-16">
        <span className='strong'>Number of invited employees</span> : {data?.eligible?.employees + data?.ineligible?.employees}

        <div className='mt-16'>Eligible for auto-raising endorsement</div>
        <div className='strong'>Employees : {data?.eligible?.employees}</div>
        <div className='strong'>Lives : {data?.eligible?.lives}</div>
        <div className='mt-16'>Endorsements subject to member confirming the details</div>
        <div className='strong'>Employees : {data?.ineligible?.employees}</div>
        <div className='strong'>Lives : {data?.ineligible?.lives}</div>
      </div>
      <Row justify="end" className="mt-56">
        <SecondaryButton onClick={cancelHandler}>{cancelButtonText}</SecondaryButton>
        <PrimaryButton onClick={confirmHandler} className="margin-left" loading={modalButtonLoading}>
          {confirmButtonText}
        </PrimaryButton>
      </Row>
    </Modal>
  );
};



export const MemberDeletionModal: React.FC<Props> = ({
  isModalVisible,
  confirmHandler,
  cancelHandler,
  modalButtonLoading,
  data,
  // buttonLoading,
}) => {
  const [form] = useForm(),
    deleteData = () => {
      form.validateFields().then((values) => {
        data.setDateOfLeaving(moment(values?.date_of_leaving))
      })
    };

  useEffect(() => {
    if (data?.dateOfLeaving && data?.data?.uuid) confirmHandler();
  }, [data.dateOfLeaving]);

  return (
    <Modal visible={isModalVisible} width={720} footer={null} onCancel={cancelHandler} destroyOnClose centered>
      <Row justify="space-around" align="middle">
        <Col span={24} className="modal-card">
          <div className="modal-icon">
            <ClearOutlined />
          </div>
          <div className="modal-title">Member Deletion</div>
          <div className="modal-subtitle">Enter a date of leaving to confirm the following deletion</div>
          <div className="mt-32" />
          <Form
            form={form}>
            <TableComponent
              columns={[
                {
                  title: 'Employee ID',
                  dataIndex: ['organisation', 'employee_id'],
                  key: 'employee_id',
                },
                {
                  title: 'Name',
                  dataIndex: 'full_name',
                  key: 'full_name',
                },
                {
                  title: 'Date of Leaving',
                  dataIndex: 'date_of_leaving',
                  key: 'date_of_leaving',
                  render: () => (
                    <>
                      &nbsp;
                      <Form.Item
                        name={['date_of_leaving']}
                        rules={[{ required: true, message: 'Please select date of leaving' }]}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          disabledDate={(current: any) => current.isAfter(moment())}
                          format="DD-MMM-YYYY"
                          suffixIcon
                          allowClear
                        />
                      </Form.Item>
                    </>
                  ),
                }
              ]}
              dataSource={[data?.data]}
              setRowKey="uuid"
            />
          </Form>
        </Col>
      </Row>
      <div className="modal-footer">
        <SecondaryButton onClick={cancelHandler}>Cancel</SecondaryButton>
        <PrimaryButton onClick={deleteData} className="margin-left" loading={modalButtonLoading}>Delete</PrimaryButton>
      </div>
    </Modal>
  );
};
const createMarkup = (html: any) => {
  return {
    __html: html,
  };
};

export const AvailSubscriptionModal: React.FC<Props> = ({
  isModalVisible,
  cancelHandler,
  logo,
  product,
  stepsToAvail,
  description,
  availType,
  coupon_code,
  getSubscriptions,
}) => {
  const [couponCode, setCouponCode] = useState(coupon_code),
    [btnLoading, setBtnLoading] = useState(false),
    [flag, setFlag] = useState(false);

  const generateCoupon = () => {
    setBtnLoading(true);

    api.products
      .getCouponCode({ product_id: product.id })
      .then((res) => {
        setCouponCode(res.data);
        setFlag(true);
        setBtnLoading(false);
      })
      .catch((err) => {
        errorHandler(err?.response?.data);
        setBtnLoading(false);
      });
  };

  useEffect(() => {
    return () => {
      if (flag) {
        getSubscriptions();
      }
    };
  }, []);

  return (
    <Modal visible={isModalVisible} footer={null} onCancel={cancelHandler} className="custom-modal" width={'560px'}>
      <div className="avail-subscription-modal">
        <div className="title">{product.name}</div>
        <div className="subtitle">{description}</div>

        {availType === 'coupon' && (
          <>
            {!couponCode && (
              <div className="generate-banner">
                <SecondaryButton loading={btnLoading} onClick={generateCoupon}>
                  Generate Coupon
                </SecondaryButton>
              </div>
            )}

            {couponCode && (
              <div className="coupon-code">
                <span className="coupon-title">Coupon Code: </span>
                <span className="coupon-code-value"> {couponCode}</span>
                <span className="copy-coupon-code">
                  <CopyToClipboard text={couponCode} onCopy={() => successNotification('Coupon code copied')}>
                    <CopyOutlined />
                  </CopyToClipboard>
                </span>
              </div>
            )}
          </>
        )}

        <div className="steps-title">Steps to avail the benefits</div>
        <div className="steps dangerous_html" dangerouslySetInnerHTML={createMarkup(stepsToAvail)}></div>

        <div className="powered-by">
          Powered by <Image src={logo} preview={false} className="provider-logo" />
        </div>
      </div>
    </Modal>
  );
};

// ...(activeMode === 'employer' && {
//   relationship_manager: {
//     full_name: relationship_managers?.policy_manager?.full_name,
//     phone: relationship_managers?.policy_manager?.phone,
//     email: relationship_managers?.policy_manager?.email,
//   }
// }),
// claim_manager: {
//   full_name: relationship_managers?.claim_manager?.full_name,
//   phone: relationship_managers?.claim_manager?.phone,
//   email: relationship_managers?.claim_manager?.email,
// },

const ESCALATION_LABELS: Record<number, string> = {
  2: 'Escalation Contact',
  3: 'Final Escalation',
};

const ESCALATION_NOTES: Record<number, string> = {
  2: 'Reach out here if Level 1 Claims Manager has not resolved your query.',
  3: 'Reach out here if the Level 1 & 2 Claims Managers have not resolved your query.',
};

const getEscalationNote = (level: number) => {
  if (ESCALATION_NOTES[level]) {
    return ESCALATION_NOTES[level];
  }
  if (level > 1) {
    const prevLevels = Array.from({ length: level - 1 }, (_, i) => i + 1);
    const prevText = prevLevels.length === 1 ? '1' : `${prevLevels.slice(0, -1).join(', ')} & ${prevLevels[prevLevels.length - 1]}`;
    return `Reach out here if Level ${prevText} Claims Manager has not resolved your query.`;
  }
  return '';
};

const escalationTheme = (level: number) => {
  if (level >= 3) return 'final';
  if (level === 2) return 'escalation';
  return 'primary';
};

const getInitials = (name?: string) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const getClaimManagers = (data: any): any[] =>
  (data?.claim_managers?.length ?? 0) > 0
    ? data.claim_managers
    : data?.claim_manager
      ? [{ ...data.claim_manager, level: 1 }]
      : [];

// Web: phone / email listed under the name, with copy on hover.
const SupportDetail: React.FC<{ href: string; value: string; icon: React.ReactNode; copyLabel: string }> = ({
  href,
  value,
  icon,
  copyLabel,
}) => (
  <div className="support-detail">
    <a href={href} className="support-detail-link" title={value}>
      <span className="support-detail-icon">{icon}</span>
      <span className="support-detail-value">{value}</span>
    </a>
    <CopyToClipboard text={value} onCopy={() => successNotification(`${copyLabel} copied`)}>
      <button type="button" className="support-detail-copy" aria-label={`Copy ${copyLabel.toLowerCase()}`} title="Copy">
        <Copy width={16} height={16} />
      </button>
    </CopyToClipboard>
  </div>
);

const SupportContactRow: React.FC<{ contact: any; role: string; level?: number; note?: string; isMobile: boolean }> = ({
  contact,
  role,
  level,
  note,
  isMobile,
}) => {
  const phone = contact?.phone ? mobileNumber(contact.phone) : '';
  const email = contact?.email;
  const name = contact?.full_name || 'your relationship manager';

  return (
    <li className={`support-contact ${level ? `tone-${escalationTheme(level)}` : 'tone-primary'}`}>
      <span className="support-avatar">{level ?? getInitials(contact?.full_name)}</span>
      <div className="support-contact-main">
        <div className="support-name">{contact?.full_name || '-'}</div>
        <div className="support-role">{level ? `Level ${level} \u00b7 ${role}` : role}</div>
        {!isMobile && (phone || email) && (
          <div className="support-details">
            {phone && (
              <SupportDetail href={`tel:${phone}`} value={phone} icon={<Phone width={18} height={18} />} copyLabel="Phone number" />
            )}
            {email && (
              <SupportDetail href={`mailto:${email}`} value={email} icon={<Mail width={18} height={18} />} copyLabel="Email" />
            )}
          </div>
        )}
        {note && <div className="support-note">{note}</div>}
      </div>
      {isMobile && (phone || email) && (
        <div className="support-quick-actions">
          {phone && (
            <a href={`tel:${phone}`} className="support-quick-action" aria-label={`Call ${name}`}>
              <Phone width={18} height={18} />
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="support-quick-action" aria-label={`Email ${name}`}>
              <Mail width={18} height={18} />
            </a>
          )}
        </div>
      )}
    </li>
  );
};

// Shared "Need Help" content — rendered in a modal on web and a bottom sheet on mobile.
const SupportContent: React.FC<{ data?: any; title: string; onClose: () => void; isMobile: boolean }> = ({
  data,
  title,
  onClose,
  isMobile,
}) => {
  const activeMode = useSelector((state: any) => state.auth?.activeMode);
  const isEmployee = activeMode !== 'employer';
  const claimManagers = getClaimManagers(data);
  const showLevels = claimManagers.length > 1;

  return (
    <div className={`support-panel ${isMobile ? 'is-mobile' : ''}`}>
      <div className="support-header">
        <div className="support-header-text">
          <div className="support-title">{title}</div>
          <div className="support-subtitle">Reach out to your relationship manager directly.</div>
        </div>
        <button type="button" className="support-close" onClick={onClose} aria-label="Close">
          <Cancel width={20} height={20} />
        </button>
      </div>

      <div className="support-body">
        {claimManagers.length > 0 && (
          <section className="support-section">
            <div className="support-section-label">
              Claims support
              {showLevels && <span className="support-section-hint">Escalate in order if unresolved</span>}
            </div>
            <ol className={`support-list ${showLevels ? 'is-stepped' : ''}`}>
              {claimManagers.map((contact: any) => (
                <SupportContactRow
                  key={`claims-${contact?.level}`}
                  contact={contact}
                  role={
                    showLevels && ESCALATION_LABELS[contact?.level]
                      ? ESCALATION_LABELS[contact?.level]
                      : 'Claims Relationship Manager'
                  }
                  level={showLevels ? contact?.level : undefined}
                  note={showLevels && isEmployee ? getEscalationNote(contact?.level) : ''}
                  isMobile={isMobile}
                />
              ))}
            </ol>
          </section>
        )}

        {data?.relationship_manager && (
          <section className="support-section">
            <div className="support-section-label">Account</div>
            <ol className="support-list">
              <SupportContactRow
                contact={data.relationship_manager}
                role="Account Relationship Manager"
                isMobile={isMobile}
              />
            </ol>
          </section>
        )}
      </div>
    </div>
  );
};

export const ContactSheet: React.FC<{ visible: boolean; onClose: () => void; data?: any; title?: string }> = ({
  visible,
  onClose,
  data,
  title = "We've got you covered!",
}) => (
  <BottomDrawer visible={visible} onClose={onClose} height="auto" className="contact-sheet">
    <SupportContent data={data} title={title} onClose={onClose} isMobile />
  </BottomDrawer>
);

export const ContactModal: React.FC<Props> = ({
  isModalVisible,
  cancelHandler,
  data,
  title = "We've got you covered!",
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <ContactSheet visible={isModalVisible} onClose={cancelHandler} data={data} title={title} />;
  }

  return (
    <Modal
      visible={isModalVisible}
      footer={null}
      closable={false}
      onCancel={cancelHandler}
      width={520}
      centered
      className="contact-modal"
    >
      <SupportContent data={data} title={title} onClose={cancelHandler} isMobile={false} />
    </Modal>
  );
};

export const ClaimDocumentsModal: React.FC<Props> = ({
  isModalVisible,
  cancelHandler,
  data,
  onViewDocument,
  onViewInstructions,
}) => {
  const processDocumentContent = (content: string) => {
    const regex = /<a\s+(?:[^>]*?\s+)?href=(["'])(https:\/\/linktr\.ee\/(?:claimsdocument|healthclaims))\1[^>]*>(.*?)<\/a>/gi;

    if (!content || !content.match(regex)) {
      return <span className='dangerous_html' dangerouslySetInnerHTML={{ __html: content }} />;
    }

    const parts = content.split(regex);
    const elements = [];

    for (let i = 0; i < parts.length; i += 4) {
      if (parts[i]) {
        elements.push(<span key={`text-${i}`} dangerouslySetInnerHTML={{ __html: parts[i] }} />);
      }

      if (i + 3 < parts.length) {
        const url = parts[i + 2];
        const text = parts[i + 3];
        const isClaimsDoc = url.toLowerCase().includes('claimsdocument');

        elements.push(
          <span
            key={`link-${i}`}
            className="link-text"
            onClick={() => {
              if (isClaimsDoc) {
                onViewDocument && onViewDocument();
              } else {
                onViewInstructions && onViewInstructions();
              }
            }}
          >
            {text}
          </span>
        );
      }
    }

    return <span className='dangerous_html'>{elements}</span>;
  };

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'heading',
      key: 'heading',
      width: '30%',
      render: (text: any, record: any) => <span className={`${record?.mandatory ? `required` : ''}`}>{record?.heading}</span>,
    },
    {
      title: 'Description',
      dataIndex: ['parameters', 'description'],
      key: 'description',
      width: '70%',
      render: (value: any, record: any) => {
        if (record?.type !== 'header') {
          return processDocumentContent(value);
        }
      }
    }],
    mobileColumns = [
      {
        title: "Document Name",
        dataIndex: 'heading',
        key: 'heading',
        width: '100%',
        render: (text: any, record: any) => {
          if (record?.type === "header") {
            return <span style={{ fontWeight: "500", padding: "8px 24px" }} className={`${record?.mandatory ? `required` : ''}`}>{record?.heading}</span>
          } else {
            return <div style={{ padding: "24px" }}>
              <div className={`${record?.mandatory ? `required` : ''}`}>{record?.heading}</div>
              <div className='mt-16'>
                {processDocumentContent(record?.parameters?.description)}
              </div>
            </div>
          }
        }
      }
    ],
    reformatData = (data: any) => {
      let temp = []
      for (let doc of data) {
        temp.push({ heading: doc.name, type: 'header' })
        temp.push(...doc.type)
      }
      return temp;
    },
    modelElement = <>
      <div className="close-cta" onClick={cancelHandler}>
        <CloseOutlined className="close-icon pointer" />
      </div>
      <Row align="top" justify="space-between" className="details-section">
        <Col>
          <div className="title">Document List - Reimbursement Claims</div>
          <div className="subtitle">GROUP HEALTH INSURANCE</div>
        </Col>
      </Row>
      <Alert type="info" message={<span>These are the documents required from you to process the claims. All documents marked with <span style={{ color: 'red' }}>*</span> are mandatory.</span>} showIcon className="mt-24 mb-16" />

      {(onViewDocument || onViewInstructions) && (
        <Row gutter={[16, 16]} className="mb-24 document-links">
          {onViewDocument && (
            <Col>
              <PrimaryButton onClick={onViewDocument} icon={<DownloadOutlined />}>
                Claims Document
              </PrimaryButton>
              <span className="link-text" onClick={onViewDocument}>
                <DownloadOutlined /> Claims Document
              </span>
            </Col>
          )}
          {onViewInstructions && (
            <Col>
              <PrimaryButton onClick={onViewInstructions} icon={<InfoCircleOutlined />}>
                Claim Instructions
              </PrimaryButton>
              <span className="link-text" onClick={onViewInstructions}>
                <InfoCircleOutlined /> Claim Instructions
              </span>
            </Col>
          )}
        </Row>
      )}

      <div className='claim-documents'>
        <TableComponent
          columns={columns}
          dataSource={reformatData(data?.filter((item: any) => item?.heading !== "Letter"))}
          showPagination={false}
          rowClassName={(record: any) => {
            if (record?.type === "header") {
              return "header-row"
            } else return null
          }}
        />
      </div>
    </>,
    drawerElement = <>
      <div className="subtitle">GROUP HEALTH INSURANCE</div>
      <Alert type="info" message={<span>These are the documents required from you to process the claims. All documents marked with <span style={{ color: 'red' }}>*</span> are mandatory.</span>} showIcon className="mt-24 mb-16" />

      {(onViewDocument || onViewInstructions) && (
        <Row gutter={[8, 8]} className="mb-24 document-links">
          {onViewDocument && (
            <Col>
              <PrimaryButton onClick={onViewDocument} icon={<DownloadOutlined />}>
                Claims Document
              </PrimaryButton>
              <span className="link-text" onClick={onViewDocument}>
                <DownloadOutlined /> Claims Document
              </span>
            </Col>
          )}
          {onViewInstructions && (
            <Col>
              <PrimaryButton onClick={onViewInstructions} icon={<InfoCircleOutlined />}>
                Claim Instructions
              </PrimaryButton>
              <span className="link-text" onClick={onViewInstructions}>
                <InfoCircleOutlined /> Claim Instructions
              </span>
            </Col>
          )}
        </Row>
      )}

      <div className='claim-documents mobile-table'>
        <TableComponent
          columns={mobileColumns}
          dataSource={reformatData(data?.filter((item: any) => item?.heading !== "Letter"))}
          showPagination={false}
          rowClassName={(record: any) => {
            if (record?.type === "header") {
              return "header-row"
            } else return null
          }}
        />
      </div>
    </>;

  return (
    <>
      <Modal width={960} visible={isModalVisible} footer={null} closable={false} onCancel={cancelHandler} className="claim-documents-modal show-on-web">
        {modelElement}
      </Modal>
      <Drawer width={'100vw'} closable={true} title={"Document List - Reimbursement Claims"} visible={isModalVisible} destroyOnClose footer={null} onClose={cancelHandler} className="claim-documents-modal show-on-mobile">
        {drawerElement}
      </Drawer>
    </>
  );
};
